/**
 * !! What exactly is "basicFields" data? !!
 *
 * The basicFields data refers to the fields data that
 * has not yet been made aware of or infused with any other data (like farms and crops).
 *
 * These hooks are meant to be used in places where fields data is required
 * before all enrichedFields dependent data (like farms and crops) is available.
 *
 */

import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";
import { BasicFieldsDataEnvelope } from "../../../models/fields";

import { useFieldsQuery } from "./fields.query";
import { fieldsAccessError } from "./fields.helpers";

// ##########################
// ## useBasicFieldsStatus ##
// ##########################

export type ResultOfUseBasicFieldsStatus = QueryStatus;
export type UseBasicFieldsStatus = () => ResultOfUseBasicFieldsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the basicFields data.
 */
export const useBasicFieldsStatus: UseBasicFieldsStatus = () => {
  return useFieldsQuery({ watch: ["status"] }).status;
};

// #########################
// ## useBasicFieldsError ##
// #########################

export type ResultOfUseBasicFieldsError = AxiosError | null;
export type UseBasicFieldsError = () => ResultOfUseBasicFieldsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the basicFields data
 * or null if no error occurred.
 */
export const useBasicFieldsError: UseBasicFieldsError = () => {
  return useFieldsQuery({ watch: ["error"] }).error;
};

// #############################
// ## useMaybeBasicFieldsData ##
// #############################

export type ResultOfUseMaybeBasicFieldsData =
  | BasicFieldsDataEnvelope
  | undefined;
export type UseMaybeBasicFieldsData = () => ResultOfUseMaybeBasicFieldsData;

/**
 * The returned value of this hook will contain
 * the basicFields data, which may be undefined
 */
export const useMaybeBasicFieldsData: UseMaybeBasicFieldsData = () => {
  return useFieldsQuery({ watch: ["data"] }).data;
};

// ########################
// ## useBasicFieldsData ##
// ########################

export type ResultOfUseBasicFieldsData = BasicFieldsDataEnvelope;
export type UseBasicFieldsData = () => ResultOfUseBasicFieldsData;

/**
 * The returned value of this hook will contain the basicFields data.
 *
 * This hook is guaranteed to contain basicFields data or will throw.
 */
export const useBasicFieldsData: UseBasicFieldsData = () => {
  const data = useMaybeBasicFieldsData();
  if (data === undefined) {
    throw fieldsAccessError;
  }
  return data;
};

// #########################
// ## useMaybeBasicFields ##
// #########################

export type ParamsOfUseMaybeBasicFields = { forceDisable?: boolean };
export type ResultOfUseMaybeBasicFields =
  QueryEnvelope<BasicFieldsDataEnvelope>;
export type UseMaybeBasicFields = (
  params?: ParamsOfUseMaybeBasicFields,
) => ResultOfUseMaybeBasicFields;

/**
 * The `data` property returned from this hook will contain
 * the basicFields data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the basicFields data.
 */
export const useMaybeBasicFields: UseMaybeBasicFields = ({
  forceDisable,
} = {}) => {
  const { status, error, data } = useFieldsQuery({
    watch: ["status", "error", "data"],
    forceDisable,
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ####################
// ## useBasicFields ##
// ####################

export type ResultOfUseBasicFields =
  EnsuredQueryEnvelope<BasicFieldsDataEnvelope>;
export type UseBasicFields = () => ResultOfUseBasicFields;

/**
 * The `data` property returned from this hook will contain
 * the basicFields data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the basicFields data.
 *
 * This hook is guaranteed to contain basicFields data or will throw.
 */
export const useBasicFields: UseBasicFields = () => {
  const { status, error, data } = useFieldsQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw fieldsAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
